import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentRiskReturnData,
  type EnrollmentRiskReturnSubmitInput,
  type EnrollmentRiskReturnSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment RiskReturn data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentRiskReturnData>
 * @example const response = await getEnrollmentRiskReturnData()
 */
export const getEnrollmentRiskReturnData = async (context?: DefaultContext): Promise<EnrollmentRiskReturnData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentRiskReturnData',
      query: `mutation getEnrollmentRiskReturnData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              experimentId
              recommendation {
                investment {
                  riskTolerance {
                    recommendedRiskTolerance
                    allowedRiskTolerances
                    riskTolerances {
                        category
                        index
                        allocation {
                            equityPercent
                            fixedIncomePercent
                            cashEquivalentPercent
                        }
                    }
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentRiskReturnData }
  return response.submitEnrollment
}

export const useGetEnrollmentRiskReturnData = createQueryHook<undefined, EnrollmentRiskReturnData>(
  getEnrollmentRiskReturnData,
)

/**
 * Submit Enrollment RiskReturn data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentRiskReturnSubmitOutput>
 * @example const response = await submitEnrollmentRiskReturnData();
 */
export const submitEnrollmentRiskReturnData = async (
  enrollmentRiskReturnSubmitInput: EnrollmentRiskReturnSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentRiskReturnSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentRiskReturnData',
      query: `mutation submitEnrollmentRiskReturnData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentRiskReturnSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentRiskReturnSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentRiskReturnData = createMutationHook<
  EnrollmentRiskReturnSubmitInput,
  EnrollmentRiskReturnSubmitOutput
>(submitEnrollmentRiskReturnData)
